import * as React from 'react'

import {
  PerformanceGoalsEmployeeCrumbDocument,
  PerformanceGoalsMetricGroupCrumbDocument,
  PerformanceGoalsTabsDocument,
} from 'generated/resources/graphql'

import i18n from 'modules/core/lib/i18n'
import { buildNavigation } from 'modules/core/lib/react-router'

import { EmployeeGoalsHeader } from 'modules/performance_goals/components/EmployeeGoalsHeader'
import { GoalHeader } from 'modules/performance_goals/components/GoalHeader'

const EmployeePage = React.lazy(() => import('modules/performance_goals/pages/EmployeePage'))
const MetricGroupPage = React.lazy(() => import('modules/performance_goals/pages/MetricGroupPage'))
const TeamDashboard = React.lazy(() => import('modules/performance_goals/pages/TeamDashboard'))
const RootPage = React.lazy(() => import('modules/performance_goals/pages/RootPage'))
const RedirectToRoutePage = React.lazy(
  () => import('modules/performance_goals/pages/RedirectToRoutePage')
)

export const performanceGoalsRoutes = buildNavigation((navigation) => {
  return [
    {
      path: navigation.performanceGoals.path.replace(`${navigation.performanceProcess.path}/`, ''),
      element: <RootPage />,
      handle: {
        meta: ({ watchQuery, session }) => {
          return watchQuery({
            query: PerformanceGoalsTabsDocument,
            variables: {
              currentEmployeeId: session?.employee?.id,
            },
          }).map(({ data }) => {
            const canSeeOtherEmployees = Boolean(data?.goals?.employeesConnection?.totalCount)
            return {
              title: i18n.t('performance_goals.pages.goals_index.title'),
              illustration: 'goals',
              description: i18n.t('performance_goals.pages.team_dashboard.description'),
              subNavigation: [
                {
                  title: i18n.t('performance_goals.tabs.people'),
                  href: navigation.performanceGoals.employees.fullPath(),
                  isVisible: canSeeOtherEmployees,
                  index: canSeeOtherEmployees,
                },
                {
                  title: i18n.t('performance_goals.sidebar.title'),
                  href: navigation.performanceGoals.groups.fullPath(),
                  isVisible: canSeeOtherEmployees,
                  index: !canSeeOtherEmployees,
                },
              ],
            }
          })
        },
      },
      children: [
        {
          index: true,
          element: <RedirectToRoutePage />,
        },
        {
          path: navigation.performanceGoals.employees.path,
          children: [
            {
              index: true,
              element: <TeamDashboard section="people" />,
            },
            {
              path: navigation.performanceGoals.employees.employee.path,
              element: <EmployeePage />,
              handle: {
                resource: true,
                meta: ({ watchQuery, params }) => {
                  if (!params.id) return { title: '', description: '' }

                  return watchQuery({
                    query: PerformanceGoalsEmployeeCrumbDocument,
                    variables: {
                      id: Number(params.id),
                    },
                  }).map(({ data }) => {
                    const goalsEmployee = data.goals?.employeesConnection?.nodes?.[0]
                    const employee = goalsEmployee?.employee

                    if (!employee) {
                      return { title: '' }
                    }

                    return {
                      title: employee.fullName ?? '',
                      description: employee.referenceContract?.jobTitle ?? '',
                      headerComponent: <EmployeeGoalsHeader goalsEmployee={goalsEmployee} />,
                    }
                  })
                },
              },
            },
          ],
        },
        {
          path: navigation.performanceGoals.groups.path,
          children: [
            {
              index: true,
              element: <TeamDashboard section="groups" />,
            },
            {
              path: navigation.performanceGoals.groups.group.path,
              element: <MetricGroupPage />,
              handle: {
                resource: true,
                meta: ({ watchQuery, params: { id } }) => {
                  if (!id) return { title: '' }

                  return watchQuery({
                    query: PerformanceGoalsMetricGroupCrumbDocument,
                    variables: {
                      id,
                    },
                  }).map(({ data }) => {
                    const metricGroup = data?.goals?.metricGroup

                    return {
                      title: metricGroup?.title ?? '',
                      description: metricGroup?.description,
                      headerComponent: <GoalHeader metricGroup={metricGroup} />,
                    }
                  })
                },
              },
            },
          ],
        },
      ],
    },
  ]
})
